// frontend/src/components/portfolio/MediaPicker.tsx
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  CircularProgress,
  Typography,
  IconButton,
  Alert,
  DialogContentText,
  TextField
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useMediaLibrary } from '../../hooks/useMediaLibrary';

interface MediaPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
  currentValue?: string;
}

// Helper function to format file size
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Unknown';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Bytes';
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

const MediaPicker: React.FC<MediaPickerProps> = ({
  open,
  onClose,
  onSelect,
  currentValue
}) => {
  const {
    assets,
    loading,
    uploading,
    error,
    page,
    totalPages,
    uploadFile,
    deleteAsset,
    loadMore
  } = useMediaLibrary(12); // Smaller limit for picker

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(currentValue || null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<{ id: string; filename: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadMetadata, setUploadMetadata] = useState({ altText: '', tags: '' });

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const metadata: any = {};
        if (uploadMetadata.altText) metadata.altText = uploadMetadata.altText;
        if (uploadMetadata.tags) metadata.tags = uploadMetadata.tags.split(',').map(t => t.trim());
        
        const uploaded = await uploadFile(file, metadata);
        setSelectedUrl(uploaded.url);
        setUploadMetadata({ altText: '', tags: '' }); // Reset
      } catch (err) {
        // Error handled in hook
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageClick = (url: string) => {
    setSelectedUrl(url);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string, filename: string) => {
    e.stopPropagation();
    setAssetToDelete({ id, filename });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (assetToDelete) {
      try {
        await deleteAsset(assetToDelete.id);
        // If deleted image was selected, clear selection
        const deletedAsset = assets.find(a => a.id === assetToDelete.id);
        if (deletedAsset && selectedUrl === deletedAsset.url) {
          setSelectedUrl(null);
        }
      } catch (err) {
        // Error already handled in hook
      }
    }
    setDeleteDialogOpen(false);
    setAssetToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setAssetToDelete(null);
  };

  const handleSelect = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedUrl(currentValue || null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Select Image</Typography>
          <IconButton edge="end" onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent dividers sx={{ p: 2 }}>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {/* Search Bar */}
          <Box mb={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by filename, tags, or alt text..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ bgcolor: 'background.paper' }}
            />
          </Box>

          {/* Upload Section */}
          <Box mb={2} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Upload New Image
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                size="small"
                label="Alt Text"
                placeholder="Describe the image..."
                value={uploadMetadata.altText}
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, altText: e.target.value }))}
                fullWidth
              />
              
              <TextField
                size="small"
                label="Tags"
                placeholder="portrait, headshot, press"
                value={uploadMetadata.tags}
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, tags: e.target.value }))}
                helperText="Comma-separated tags"
                fullWidth
              />
              
              <Button
                fullWidth
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Choose File & Upload'}
              </Button>
            </Box>
          </Box>

          {/* Error display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Loading state */}
          {loading && assets.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress />
            </Box>
          ) : assets.length === 0 ? (
            // Empty state
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
              gap={2}
            >
              <Typography variant="body1" color="text.secondary">
                No images available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload your first image to get started
              </Typography>
            </Box>
          ) : (
            // Image Grid
            <>
              <Grid container spacing={2}>
                {assets
                  .filter(asset => {
                    if (!searchQuery) return true;
                    const query = searchQuery.toLowerCase();
                    return (
                      asset.filename.toLowerCase().includes(query) ||
                      asset.altText?.toLowerCase().includes(query) ||
                      asset.tags?.some(tag => tag.toLowerCase().includes(query))
                    );
                  })
                  .map((asset) => (
                  <Grid item xs={6} sm={4} md={3} key={asset.id}>
                    <Card
                      sx={{
                        position: 'relative',
                        border: selectedUrl === asset.url ? '3px solid #1976d2' : '1px solid #e0e0e0',
                        transition: 'all 0.2s'
                      }}
                    >
                      <CardActionArea onClick={() => handleImageClick(asset.url)}>
                        <CardMedia
                          component="img"
                          height="120"
                          image={asset.url}
                          alt={asset.altText || asset.filename}
                          sx={{ objectFit: 'cover' }}
                        />
                        {selectedUrl === asset.url && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              p: 0.5,
                              display: 'flex'
                            }}
                          >
                            <CheckIcon sx={{ color: 'white', fontSize: 20 }} />
                          </Box>
                        )}
                      </CardActionArea>

                      {/* Delete button */}
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          bgcolor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(211, 47, 47, 0.8)',
                          }
                        }}
                        onClick={(e) => handleDeleteClick(e, asset.id, asset.filename)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>

                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="caption"
                          noWrap
                          title={asset.filename}
                          sx={{ display: 'block' }}
                        >
                          {asset.filename}
                        </Typography>
                        
                        {/* Alt Text */}
                        {asset.altText && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}
                            noWrap
                            title={asset.altText}
                          >
                            {asset.altText}
                          </Typography>
                        )}
                        
                        {/* Tags */}
                        {asset.tags && asset.tags.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {asset.tags.slice(0, 2).map((tag, idx) => (
                              <Typography
                                key={idx}
                                variant="caption"
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                  fontSize: '0.65rem'
                                }}
                              >
                                {tag}
                              </Typography>
                            ))}
                            {asset.tags.length > 2 && (
                              <Typography variant="caption" color="text.secondary">
                                +{asset.tags.length - 2}
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        {/* Metadata */}
                        <Box sx={{ mt: 0.5 }}>
                          {asset.metadata?.width && asset.metadata?.height && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {asset.metadata.width} × {asset.metadata.height}
                            </Typography>
                          )}
                          {asset.metadata?.size && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {formatFileSize(asset.metadata.size)}
                            </Typography>
                          )}
                          {asset.metadata?.format && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase' }}>
                              {asset.metadata.format}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Load More Button */}
              {page < totalPages && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {/* Actions */}
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSelect}
            disabled={!selectedUrl}
          >
            Select Image
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{assetToDelete?.filename}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MediaPicker;